<template>
  <div class="flex flex-col gap-2 overflow-hidden h-full">
    <ThreadsNav show-back>
      <template #center>
        <h1 class="text-base font-semibold">
          {{ $t("Dungeons.sprite.create") }}
        </h1>
      </template>
      <template #right>
        <button class="btn btn-primary btn-xs" @click="clickSave">
          {{ $t("Buttons.save") }}
        </button>
      </template>
    </ThreadsNav>
    <div class="flex-1 overflow-y-auto">
      <VanCellGroup inset>
        <VanField
          :label="$t('Dungeons.sprite.name')"
          v-model="sprite.name"
          :name="$t('Dungeons.sprite.name')"
          :placeholder="$t('Dungeons.sprite.rules.name')"
          :rules="[
            { required: true, message: $t('Dungeons.sprite.rules.name') },
          ]"
          required
        />

        <VanField
          :model-value="$t(`Dungeons.sprite.modeOption.${sprite.mode}`)"
          :label="$t('Dungeons.sprite.mode')"
          :name="$t('Dungeons.sprite.mode')"
          :placeholder="$t('Dungeons.sprite.rules.mode')"
          :rules="[
            { required: true, message: $t('Dungeons.sprite.rules.mode') },
          ]"
          is-link
          readonly
          required
          @click="showModePicker = true"
        />

        <VanField
          v-if="sprite.mode === MateSpriteMode.Prompt"
          required
          :label="$t('Dungeons.sprite.modeOption.1')"
          :model-value="sprite.prompt"
          :name="$t('Dungeons.sprite.prompt')"
          :placeholder="$t('Dungeons.sprite.rules.prompt')"
          autosize
          label-align="top"
          type="textarea"
          @update:modelValue="sprite.prompt = $event"
        />
        <div class="bg px-4 py-2.5" v-if="sprite.mode === MateSpriteMode.Image">
          <h3 class="required mb-2.5">
            {{ $t("Dungeons.sprite.modeOption.2") }}
          </h3>

          <div
            class="w-full aspect-[16/9] flex justify-center items-center border border-base-content/10 rounded-xl relative overflow-hidden max-w-[20rem]"
          >
            <user-avatar
              v-if="imageMedia?.id"
              :avatar-url="imageMedia.url"
              aspect="16/9"
              class="w-full"
            />
            <div
              class="absolute top-0 left-0 w-full h-full flex flex-col gap-2 items-center justify-center bg-base-100/50"
            >
              <div class="flex flex-col gap-2">
                <button
                  class="btn btn-primary rounded font-normal"
                  @click="showImageUploadPopup = true"
                >
                  {{ $t("trait.select") }}
                </button>
              </div>
            </div>
          </div>
        </div>
        <div
          class="bg px-4 py-2.5"
          v-if="sprite.mode === MateSpriteMode.MateTrait"
        >
          <div class="flex justify-between items-center mb-2.5">
            <h3 class="required">{{ $t("Dungeons.sprite.modeOption.3") }}</h3>
            <button
              class="btn btn-primary rounded font-normal btn-xs"
              @click="showSelectMateModal = true"
            >
              {{ $t("Buttons.select") }}
            </button>
          </div>

          <div class="flex justify-center items-center">
            <user-avatar
              v-if="_mateTrait?.id"
              :avatar-url="_mateTrait?.media?.url"
              class="w-[88px]"
            />
          </div>
        </div>
      </VanCellGroup>

      <div class="flex justify-end p-4">
        <button
          class="btn btn-sm btn-primary"
          @click="clickGenerate"
          :disabled="loading"
        >
          {{
            loading
              ? $t("Dungeons.sprite.status.generating")
              : _frameMedia?.id
              ? $t("Dungeons.sprite.status.regenerate")
              : $t("Dungeons.sprite.status.generate")
          }}
        </button>
      </div>

      <div class="flex flex-col gap-2 p-4" v-if="_frameMedia?.id || loading">
        <h3 class="mb-1">Frame</h3>
        <div v-if="!loading">
          <img
            :src="_frameMedia?.url"
            class="w-full aspect-[384/320] rounded-lg shrink-0"
            crossorigin="anonymous"
            alt="frame"
          />
        </div>
        <div v-else>
          <div
            class="skeleton-loading-bg w-full aspect-[384/320] rounded-lg shrink-0"
          />
        </div>
      </div>

      <div class="flex flex-col gap-2 p-4" v-if="_animations.length || loading">
        <h3 class="mb-1">Preview</h3>
        <div
          v-if="!loading"
          class="border border-base-content/10 rounded-xl overflow-hidden py-4 px-5 aspect-[384/320]"
        >
          <DungeonsSpriteAnimations :animations="_animations" />
        </div>
        <div v-else>
          <div
            class="skeleton-loading-bg w-full aspect-[96/107] rounded-lg shrink-0"
          />
        </div>
      </div>
    </div>
    <ModalSelectOption
      v-model:show="showModePicker"
      :options="modeOptions"
      @confirm="(sprite.mode = $event), (showModePicker = false)"
    />
    <media-select-popup
      v-model:show="showImageUploadPopup"
      :allow-crop-outside="false"
      :enable-crop="true"
      :enableGoImageGen="false"
      :max-count="1"
      :multiple="false"
      :upload-type="0"
      media-type="img"
      replace
      @on-media-selected="onImageSelected"
    />

    <ModalSelectMate
      v-if="mate?.id"
      v-model:show="showSelectMateModal"
      :selected-trait-id="sprite.mateTraitId"
      :mate-id="mate.id"
      @update:selectedTrait="onSelectMateTrait"
    />
  </div>
</template>

<script setup lang="ts">
import {
  createMateSprite,
  CreateMateSpriteParams,
  createMateSpriteTask,
  readMateSpriteTask,
  updateMateSprite,
} from "@/apis/script-game";
import { MediaUploadFileItem } from "@/types/component";
import {
  MateSprite,
  MateSpriteAnimation,
  MateSpriteMode,
  MateSpriteTaskStatus,
} from "@/types/game";
import { showFailToast, showLoadingToast, showSuccessToast } from "vant";
import { computed, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import DungeonsSpriteAnimations from "@/components/Dungeons/DungeonsSpriteAnimations.vue";
import { useMate } from "@/stores/mate";
import router from "@/router";
import { useRoute } from "vue-router";
import { useDungeon } from "@/stores/dungeon";
import { Mate } from "@/types/entity";
const sprite = ref<MateSprite>({
  name: "",
  mode: MateSpriteMode.MateTrait,
  prompt: "",
});

const { t } = useI18n();
const mateStore = useMate();
const dungeonStore = useDungeon();
const showModePicker = ref(false);
const imageMedia = ref<Media>();
const showImageUploadPopup = ref(false);
const loading = ref(false);
const taskId = ref<string>();
const modeOptions = [
  { text: t("Dungeons.sprite.modeOption.1"), value: MateSpriteMode.Prompt },
  { text: t("Dungeons.sprite.modeOption.2"), value: MateSpriteMode.Image },
  { text: t("Dungeons.sprite.modeOption.3"), value: MateSpriteMode.MateTrait },
];
const showSelectMateModal = ref(false);
const clickGenerate = async () => {
  if (sprite.value.mode === MateSpriteMode.Prompt && !sprite.value.prompt)
    return showFailToast(t("Dungeons.sprite.rules.prompt"));

  if (
    sprite.value.mode === MateSpriteMode.MateTrait &&
    !sprite.value.mateTraitId
  )
    return showFailToast(t("Dungeons.sprite.rules.mateTrait"));

  if (sprite.value.mode === MateSpriteMode.Image && !imageMedia.value?.id)
    return showFailToast(t("Dungeons.sprite.rules.image"));

  loading.value = true;
  try {
    const res = await createMateSpriteTask(sprite.value);
    taskId.value = res.data.taskId;
    pollTaskStatus();
  } catch (e) {
    loading.value = false;
    // showFailToast(t("Dungeons.sprite.fail"));
  }
};

// 轮询任务状态
const pollTaskStatus = async () => {
  try {
    const task = await readMateSpriteTask(taskId.value!);
    if (task.data.status === MateSpriteTaskStatus.Processing) {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      pollTaskStatus();
    } else if (task.data.status === MateSpriteTaskStatus.Completed) {
      loading.value = false;
      initSprint(task.data);
    } else if (task.data.status === MateSpriteTaskStatus.Failed) {
      loading.value = false;
      showFailToast(t("Dungeons.sprite.fail"));
    }
  } catch (e) {
    console.log(e);
    await new Promise((resolve) => setTimeout(resolve, 1000));
    pollTaskStatus();
  }
};

const _frameMedia = ref<Media>();
const _animations = ref<MateSpriteAnimation[]>([]);
const initSprint = (data: any) => {
  const { frameMedia, animations } = data;
  _frameMedia.value = {
    id: frameMedia.id,
    url: frameMedia.url,
  };
  _animations.value = animations;
};

const onImageSelected = (mediaList: MediaUploadFileItem[]) => {
  const media = mediaList[0];
  sprite.value.imageMediaId = media.mediaId;
  imageMedia.value = {
    id: media.mediaId,
    url: media.url!,
  };
};

const _mateTrait = ref<MateTrait>();
const onSelectMateTrait = (mateTrait: MateTrait | null) => {
  if (mateTrait) {
    sprite.value.mateTraitId = mateTrait.id!;
    _mateTrait.value = mateTrait;
    showSelectMateModal.value = false;
  }
};

const clickSave = async () => {
  const loading = showLoadingToast(t(`Message.saving`));
  const params: CreateMateSpriteParams = {
    mateId: mate.value!.id!,
    frameMediaId: _frameMedia.value?.id,
    imageMediaId: imageMedia.value?.id,
    isDefault: true,
    name: sprite.value.name!,
    mode: sprite.value.mode!,
    prompt: sprite.value.prompt!,
  };
  try {
    if (sprite.value.id) {
      await updateMateSprite(sprite.value.id, params);
    } else {
      await createMateSprite(params);
    }
    await dungeonStore.fetchMateSpriteList(mate.value!.id!, true);

    showSuccessToast(t("Dungeons.sprite.success"));
    dungeonStore.selectedMateUuid = "";
    setTimeout(() => {
      router.back();
    }, 1000);
  } catch (e) {
    loading.close();
    showFailToast(t("Message.saveFailed"));
  }
};

const route = useRoute();
const mate = ref<Mate>();

watch(
  () => mateStore.mateMap,
  (newVal) => {
    if (!mate.value) {
      mate.value = newVal.get(route.params.uuid as string);
    }
  },
  {
    deep: true,
  }
);

onMounted(async () => {
  if (route.params.uuid && mateStore.mateMap.size > 0) {
    mate.value = mateStore.mateMap.get(route.params.uuid as string);
  }

  // if (editSprite) {
  //   sprite.value = editSprite;
  //   _frameMedia.value = editSprite.frameMedia;
  //   _animations.value = editSprite.animationzz;
  //   _mateTrait.value = editSprite.mateTrait;
  //   taskId.value = editSprite.taskId;
  // }
});
</script>

<style scoped>
.bg {
  background: var(--van-cell-background);
}
.required::before {
  content: "*";
  color: var(--van-field-required-mark-color);
  margin-right: 2px;
}
</style>
